<template>
  <div class="login-bridge">
    <main v-if="!completion || errorMessage" class="login-card" aria-labelledby="login-title">
      <p class="eyebrow">AUTOSQUARE ACCOUNT</p>
      <h1 id="login-title">{{ title }}</h1>
      <p>{{ description }}</p>
      <p v-if="errorMessage" class="login-error" role="alert">{{ errorMessage }}</p>
      <button v-if="errorMessage" type="button" class="login-button" :disabled="loading" @click="retryLogin">
        {{ loading ? '正在处理…' : '重新登录' }}
      </button>
    </main>
    <p v-else class="sr-only" role="status">正在安全完成登录</p>
  </div>
</template>

<script>
import {
  clearBrowserLoginState,
  loadBrowserLoginState,
  safeReturnPath,
} from '../auth/browser-session.mjs'
import { useAuthStore } from '../store/auth'

export default {
  name: 'LoginBridgeView',
  props: {
    completion: { type: Boolean, default: false }
  },
  data: () => ({ loading: false, errorMessage: '' }),
  computed: {
    title() {
      if (this.completion && this.errorMessage) return '登录未完成'
      return this.completion ? '正在完成登录' : '正在前往账号中心'
    },
    description() {
      if (this.completion && this.errorMessage) return '未能安全领取登录结果，请重新登录。'
      return this.completion
        ? '正在安全领取登录结果，完成后将自动返回官网。'
        : '正在建立安全登录会话，请稍候。'
    }
  },
  mounted() {
    if (this.completion) this.completeLogin()
    else this.beginLogin()
  },
  methods: {
    async beginLogin() {
      if (this.loading) return
      this.loading = true
      this.errorMessage = ''
      const returnPath = safeReturnPath(this.$route.query.return_to || '/')
      try {
        const result = await useAuthStore().beginBrowserLogin(returnPath)
        if (result?.return_path) await this.$router.replace(result.return_path)
      } catch (error) {
        this.errorMessage = error.message || '无法前往账号中心，请稍后重试。'
      } finally {
        this.loading = false
      }
    },
    async completeLogin() {
      if (this.loading) return
      this.loading = true
      this.errorMessage = ''
      try {
        const returnPath = await useAuthStore().completeBrowserLogin()
        await this.$router.replace(returnPath)
      } catch (error) {
        this.errorMessage = error.message || '无法完成登录，请重新登录。'
      } finally {
        this.loading = false
      }
    },
    async retryLogin() {
      if (!this.completion) {
        await this.beginLogin()
        return
      }
      const pending = loadBrowserLoginState(window.sessionStorage)
      const returnPath = pending?.return_path || '/'
      clearBrowserLoginState(window.sessionStorage)
      await this.$router.replace({ path: '/login', query: { return_to: returnPath } })
      await this.beginLogin()
    }
  }
}
</script>

<style scoped>
.login-bridge {
  display: grid;
  min-height: 100vh;
  place-items: center;
  padding: 24px;
  background: linear-gradient(160deg, #f4f7ff 0%, #eef2ff 45%, #f8fafc 100%);
}
.login-card {
  width: min(480px, 100%);
  padding: 46px 40px;
  border: 1px solid rgba(99, 102, 241, 0.18);
  border-radius: 24px;
  text-align: center;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 18px 50px rgba(79, 70, 229, 0.12);
}
.eyebrow {
  color: #4f46e5;
  font-size: 0.72rem;
  font-weight: 760;
  letter-spacing: 0.15em;
}
h1 {
  margin-top: 8px;
  color: #0f172a;
  font-size: 2rem;
}
.login-card > p:not(.eyebrow, .login-error) {
  margin: 14px auto 26px;
  color: #64748b;
  line-height: 1.75;
}
.login-error {
  margin: 0 0 18px;
  color: #dc2626;
  font-size: 0.9rem;
}
.login-button {
  display: inline-flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  padding: 0 22px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  font-weight: 680;
  cursor: pointer;
}
.login-button:disabled {
  cursor: wait;
  opacity: 0.68;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@media (max-width: 520px) {
  .login-card { padding: 38px 22px; }
}
</style>
